import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import {
    IIdmFeature,
    IIdmRole,
    IIdmStaffMember,
    IIdmUserFeatureGrant,
    idmService,
} from "../../../entities/idm";

type TFeatureSearchItem = IIdmFeature & {name: string};

interface IUseStaffAccessManagementParams {
    enabled: boolean;
    selectedStaffId: number | null;
    staffMember: IIdmStaffMember;
    setStaffMember: Dispatch<SetStateAction<IIdmStaffMember>>;
    roles: IIdmRole[];
    canListUserFeatures: boolean;
    canListAvailableFeatures: boolean;
    refreshBase: () => Promise<void>;
    onError: (error: unknown, defaultMessage: string) => void;
    onSuccess: (message: string) => void;
}

export const useStaffAccessManagement = ({
    enabled,
    selectedStaffId,
    staffMember,
    setStaffMember,
    roles,
    canListUserFeatures,
    canListAvailableFeatures,
    refreshBase,
    onError,
    onSuccess,
}: IUseStaffAccessManagementParams) => {
    const [isFeatureListOpen, setIsFeatureListOpen] = useState(false)
    const [featureListSearch, setFeatureListSearch] = useState('')
    const [isUserFeaturesLoading, setIsUserFeaturesLoading] = useState(false)
    const [isAvailableFeaturesLoading, setIsAvailableFeaturesLoading] = useState(false)
    const [isUserFeaturesLoaded, setIsUserFeaturesLoaded] = useState(false)
    const [availableFeatures, setAvailableFeatures] = useState<IIdmFeature[]>([])
    const [rolesModalOpen, setRolesModalOpen] = useState(false)
    const [featuresModalOpen, setFeaturesModalOpen] = useState(false)
    const [roleIdsToAssign, setRoleIdsToAssign] = useState<number[]>([])
    const [featureIdsToGrant, setFeatureIdsToGrant] = useState<number[]>([])
    const [featureSearchItems, setFeatureSearchItems] = useState<TFeatureSearchItem[]>([])
    const [userFeatureGrants, setUserFeatureGrants] = useState<IIdmUserFeatureGrant[]>([])

    const resetAccessState = () => {
        setAvailableFeatures([])
        setUserFeatureGrants([])
        setIsUserFeaturesLoaded(false)
        setIsFeatureListOpen(false)
        setFeatureListSearch('')
    }

    useEffect(() => {
        resetAccessState()
    }, [selectedStaffId])

    const loadUserFeatureGrants = async (currentUserId: number) => {
        try{
            setIsUserFeaturesLoading(true)
            const response = await idmService.listUserFeatureGrants(currentUserId)
            setUserFeatureGrants(response.feature_grants)
            setStaffMember(prev => ({
                ...prev,
                effective_features: response.feature_grants.map(grant => ({
                    feature_id: grant.feature_id,
                    feature_key: grant.feature_key,
                    feature_name: grant.feature_name,
                    category: grant.category,
                }))
            }))
            setIsUserFeaturesLoaded(true)
        }
        catch(error){
            onError(error, 'Ошибка при получении выданных фич сотрудника')
        }
        finally{
            setIsUserFeaturesLoading(false)
        }
    }

    useEffect(() => {
        if(!enabled || !selectedStaffId || !isFeatureListOpen || isUserFeaturesLoaded || !canListUserFeatures){
            return
        }
        loadUserFeatureGrants(selectedStaffId)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canListUserFeatures, enabled, isFeatureListOpen, isUserFeaturesLoaded, selectedStaffId])

    useEffect(() => {
        if(!featuresModalOpen || !selectedStaffId || !canListAvailableFeatures){
            return
        }

        const load = async () => {
            try{
                setIsAvailableFeaturesLoading(true)
                const response = await idmService.listUserAvailableFeatures(selectedStaffId)
                setAvailableFeatures(response.features)
            }
            catch(error){
                onError(error, 'Ошибка при получении доступных для выдачи фич')
            }
            finally{
                setIsAvailableFeaturesLoading(false)
            }
        }
        load()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canListAvailableFeatures, featuresModalOpen, selectedStaffId])

    const roleOptions = useMemo(
        () => roles.filter(role => !staffMember.roles.some(userRole => userRole.role_id === role.id)),
        [roles, staffMember.roles]
    )
    const featureOptions = useMemo<TFeatureSearchItem[]>(
        () => availableFeatures.map(feature => ({
            ...feature,
            name: `${feature.name} ${feature.key} ${feature.description}`
        })),
        [availableFeatures]
    )
    const filteredEffectiveFeatures = useMemo(() => {
        const normalizedSearch = featureListSearch.trim().toLowerCase()
        if(!normalizedSearch){
            return staffMember.effective_features
        }
        return staffMember.effective_features.filter(feature => (
            `${feature.feature_name} ${feature.feature_key} ${feature.category}`.toLowerCase().includes(normalizedSearch)
        ))
    }, [featureListSearch, staffMember.effective_features])

    useEffect(() => setFeatureSearchItems(featureOptions), [featureOptions])

    const onAssignRoles = async () => {
        if(!selectedStaffId || roleIdsToAssign.length === 0){
            setRolesModalOpen(false)
            return
        }
        try{
            await Promise.all(roleIdsToAssign.map(roleId => idmService.assignUserRole(selectedStaffId, roleId)))
            setRoleIdsToAssign([])
            setRolesModalOpen(false)
            await refreshBase()
            onSuccess('Роли сотруднику назначены')
        }
        catch(error){
            onError(error, 'Ошибка при назначении ролей сотруднику')
        }
    }

    const onRemoveRole = async (roleId: number) => {
        if(!selectedStaffId) return
        try{
            await idmService.removeUserRole(selectedStaffId, roleId)
            await refreshBase()
            onSuccess('Роль у сотрудника снята')
        }
        catch(error){
            onError(error, 'Ошибка при снятии роли у сотрудника')
        }
    }

    const onGrantFeatures = async () => {
        if(!selectedStaffId || featureIdsToGrant.length === 0){
            setFeaturesModalOpen(false)
            return
        }
        try{
            await Promise.all(featureIdsToGrant.map(featureId => idmService.grantUserFeature(selectedStaffId, featureId)))
            setFeatureIdsToGrant([])
            setFeaturesModalOpen(false)
            await loadUserFeatureGrants(selectedStaffId)
            onSuccess('Доступы сотруднику выданы')
        }
        catch(error){
            onError(error, 'Ошибка при выдаче feature-доступов')
        }
    }

    const onRevokeFeature = async (featureId: number) => {
        if(!selectedStaffId) return
        try{
            await idmService.revokeUserFeature(selectedStaffId, featureId)
            setStaffMember(prev => ({
                ...prev,
                effective_features: prev.effective_features.filter(feature => feature.feature_id !== featureId)
            }))
            setUserFeatureGrants(prev => prev.filter(grant => grant.feature_id !== featureId))
            onSuccess('Доступ у сотрудника отозван')
        }
        catch(error){
            onError(error, 'Ошибка при отзыве feature-доступа')
        }
    }

    return {
        featureIdsToGrant,
        featureListSearch,
        featureOptions,
        featureSearchItems,
        featuresModalOpen,
        filteredEffectiveFeatures,
        isAvailableFeaturesLoading,
        isFeatureListOpen,
        isUserFeaturesLoading,
        onAssignRoles,
        onGrantFeatures,
        onRemoveRole,
        onRevokeFeature,
        roleIdsToAssign,
        roleOptions,
        rolesModalOpen,
        setFeatureIdsToGrant,
        setFeatureListSearch,
        setFeatureSearchItems,
        setFeaturesModalOpen,
        setIsFeatureListOpen,
        setRoleIdsToAssign,
        setRolesModalOpen,
        userFeatureGrants,
    }
}

export type TStaffAccessManagement = ReturnType<typeof useStaffAccessManagement>
